import * as React from 'react';
import * as Metadata from '../../entities/Metadata';
import { ItemCreation } from '../common/ItemCreation';
import { Validator } from "../../validation";
import Spinner from "../common/Spinner";
import { Overlay, Toggle, TextField, IDropdownOption } from 'office-ui-fabric-react';
import SelectSettingsEdit from './SelectSettingsEdit';
import { SliderSettingsEdit } from './SliderSettingsEdit';
import { IdentifierSettingsEdit } from './IdentifierSettingsEdit';
import { NumberSettingsEdit } from './NumberSettingsEdit';
import { Dictionary, EntityType, Impact } from '../../entities/common';
import { ResourceSettingsEdit } from './ResourceSettingsEdit';
import { EnumSliderSettingsEdit, impactEnumSliderDefaultSettings } from './EnumSliderSettingsEdit';
import { FieldsService } from '../common/FieldsService';
import { FieldTypeDrowdown } from './FieldTypeDrowdown';
import StatusSelectSettingsEdit from './ConfigureFields/StatusSelectSettingsEdit';
import StageSelectSettingsEdit from './ConfigureFields/StageSelectSettingsEdit';

const validators = {
    label: Validator.new().required().build()
}

interface IFieldSettings extends Dictionary<any> {
    required: boolean;
}

export interface IBaseFieldInfo {
    label: string;
    description?: string;
    settings: IFieldSettings;
}

export interface IFieldActions {
    removeField?: (fieldId: string) => void;
    saveField?: (fieldInfo: Metadata.ICreateFieldInfo) => void;
    updateField?: (fieldId: string, fieldInfo: Metadata.IUpdateFieldInfo) => void;
}

export interface IFieldPanelProps {
    field?: Metadata.Field;
    customWidth?: string;
    showSpinner?: boolean;
    allowManageFields?: boolean;
    entityType: EntityType;
    actions: IFieldActions;
    onDismiss: () => void;
}

enum FieldDisplayType {
    Text,
    Richtext,
    Date,
    Integer,
    Decimal,
    User,
    Resource,
    Slider,
    Select,
    Flag,
    Impact,
    Portfolio,
    Project,
    Challenge,
    Idea,
    Group,
    Task,
    Hyperlink,
    Predecessor,
    Assignment,
    CostRateTable,
    Program,
    Objective,
    Roadmap,
    Email,
    Clock,
    AdministrateApp,
    ManagePrioritization,
    ManageBudget,
    ManageConfiguration,
    ManageBilling,
    ManageIntegrations,
    PPMInsights,
    ManageResources,
    ManageResourcePlanning,
    PresentationMode,
    LogonAccount,
    License,
    AuthProvider,
    LinkedResource,
}

export enum FieldActionTypes {
    Create = 'CREATE',
    Edit = 'EDIT'
}

export interface IFieldPanelState {
    isDirty?: boolean;
    fieldInfo: IBaseFieldInfo;
    displayType: FieldDisplayType;
    displayTypes: IDropdownOption[];
    isSettingsValid: boolean;
    actionType: FieldActionTypes;
}

function toFieldInfo(field: Metadata.Field): IBaseFieldInfo {
    return {
        label: Metadata.getLabel(field),
        description: field.description,
        settings: {
            required: field.settings?.required
        }
    };
}

export interface ISettingsEditComponent {
    save: (fieldInfo: Metadata.ICreateFieldInfo) => void;
    update: (fieldInfo: IBaseFieldInfo) => Metadata.IUpdateFieldInfo | void;
}

export default class FieldPanel extends React.Component<IFieldPanelProps, IFieldPanelState> {
    private _settingComponent = React.createRef<ISettingsEditComponent>();

    constructor(props: IFieldPanelProps) {
        super(props);
        const denyCreationTypes: FieldDisplayType[] = [
            FieldDisplayType.Portfolio, FieldDisplayType.Project,
            FieldDisplayType.Challenge, FieldDisplayType.Idea,
            FieldDisplayType.Group, FieldDisplayType.CostRateTable, FieldDisplayType.Task,
            FieldDisplayType.Predecessor, FieldDisplayType.Assignment,
            FieldDisplayType.Program, FieldDisplayType.Objective, FieldDisplayType.Roadmap,
            FieldDisplayType.Email, FieldDisplayType.Clock,
            FieldDisplayType.AdministrateApp, FieldDisplayType.ManagePrioritization,
            FieldDisplayType.ManageBudget, FieldDisplayType.ManageConfiguration,
            FieldDisplayType.ManageBilling, FieldDisplayType.ManageIntegrations,
            FieldDisplayType.PPMInsights, FieldDisplayType.ManageResources,
            FieldDisplayType.ManageResourcePlanning, FieldDisplayType.PresentationMode,
            FieldDisplayType.LogonAccount, FieldDisplayType.License,
            FieldDisplayType.AuthProvider, FieldDisplayType.LinkedResource];
        this.state = {
            fieldInfo: props.field
                ? toFieldInfo(props.field)
                : {
                    label: '',
                    description: '',
                    settings: {
                        required: false
                    }
                },
            displayType: props.field ? getDisplayType(props.field) : FieldDisplayType.Text,
            displayTypes: Object.keys(FieldDisplayType)
                .filter(k => typeof FieldDisplayType[k as any] === "number"
                    && (props.field ? true : !denyCreationTypes.includes((FieldDisplayType[k as any]) as any as FieldDisplayType)))
                .map(k => {
                    const key = FieldDisplayType[k as any] as any as FieldDisplayType;
                    const descr = getDisplayTypeDescriptor(key);
                    return {
                        key: key,
                        text: descr?.label ?? k,
                        data: descr
                    };
                }),
            isSettingsValid: true,
            actionType: props.field ? FieldActionTypes.Edit : FieldActionTypes.Create
        }
    }

    public render() {
        const { fieldInfo, isSettingsValid } = this.state;
        return (
            <ItemCreation
                onDismiss={this.props.onDismiss}
                header={{
                    text: `${this.props.field ? 'Configure' : 'Create'} Field`,
                    secondaryText: 'Configure field display options',
                    nameEditorLabel: "Field Name",
                    onChanged: label => this._setFieldInfo({ label: label }),
                    validator: validators.label,
                    value: fieldInfo.label
                }}
                isDirty={this.state.isDirty}
                commands={[
                    {
                        primary: true,
                        text: `${this.props.field ? 'Save' : 'Create'} Field`,
                        onClick: this._saveClick,
                        disabled: !isSettingsValid || !Validator.isValid(validators, fieldInfo) || !this.props.allowManageFields
                    }, {
                        text: 'Cancel',
                        onClick: this.props.onDismiss
                    }
                ]}
                className={`field-panel ${this.state.isDirty ? " not-saved" : ""}`}
                customWidth={this.props.customWidth}>
                {this._renderDetails()}
                {this.props.showSpinner && <Overlay><Spinner /></Overlay>}
            </ItemCreation>
        );
    }

    private _renderDetails() {
        const { field } = this.props;
        const { fieldInfo, displayType, displayTypes } = this.state;

        return <div className="field-details">
            <Toggle label='Required'
                checked={fieldInfo.settings.required || field?.isSystem}
                disabled={field && (field.isReadonly || field.isSystem)}
                onChange={(e, checked) => this._setFieldInfo({ settings: { required: !!checked } })}
                onText='Yes'
                offText='No' />
            <TextField label="Tooltip Description"
                onChange={(e, value) => this._setFieldInfo({ description: value })}
                value={fieldInfo.description ?? ""}
                resizable={false}
                multiline={true}
                rows={6} />
            <FieldTypeDrowdown
                disabled={!!this.props.field}
                label={'Type'}
                required={true}
                selectedKey={displayType}
                onChange={(e, option) => option && this.setState({
                    displayType: option.key as FieldDisplayType,
                    isSettingsValid: true,
                    isDirty: true
                })}
                defaultSelectedKeys={[0]}
                placeholder={'Select type'}
                options={displayTypes}
            />
            {
                this._renderSettings(displayType)
            }
        </div>
    }

    private _renderSettings = (displayType: FieldDisplayType): JSX.Element | null => {
        const { field, entityType } = this.props;
        const { actionType } = this.state;

        const settings = field?.settings ?? {};
        switch (displayType) {
            case FieldDisplayType.Text:
                if (field && FieldsService.isIdentifier(field)) {
                    return <IdentifierSettingsEdit
                        ref={this._settingComponent as any}
                        actionType={actionType}
                        settings={settings}
                        onChange={this._onSettingsChange}
                    />;
                }

                return null;
            case FieldDisplayType.Slider:
                return <SliderSettingsEdit
                    ref={this._settingComponent as any}
                    disabled={field?.isSystem}
                    isInteger={field?.type == Metadata.FieldType.Integer}
                    actionType={actionType}
                    settings={settings}
                    onChange={this._onSettingsChange}
                />;
            case FieldDisplayType.Select:
                if (field?.settings?.editControl === "ColorStatusDropdown") {
                    return <StatusSelectSettingsEdit
                        ref={this._settingComponent}
                        actionType={actionType}
                        entityType={entityType}
                        settings={settings as any}
                        onChange={this._onSettingsChange}
                    />;
                }
                if (field?.settings?.editControl === "ColorStageDropdown") {
                    return <StageSelectSettingsEdit
                        ref={this._settingComponent}
                        actionType={actionType}
                        entityType={entityType}
                        settings={settings as any}
                        onChange={this._onSettingsChange}
                    />;
                }
                return <SelectSettingsEdit
                    ref={this._settingComponent}
                    actionType={actionType}
                    settings={settings as any}
                    onChange={this._onSettingsChange}
                />;
            case FieldDisplayType.Integer:
            case FieldDisplayType.Decimal:
                if (settings.editControl === 'ColorStatusDropdown') {
                    return null;
                }
                return <NumberSettingsEdit
                    ref={this._settingComponent as any}
                    isNative={field ? field.isNative : false}
                    actionType={actionType}
                    settings={settings}
                    onChange={this._onSettingsChange}
                />;
            case FieldDisplayType.Assignment:
            case FieldDisplayType.User:
            case FieldDisplayType.Resource:
                return <ResourceSettingsEdit
                    ref={this._settingComponent as any}
                    actionType={actionType}
                    settings={settings}
                    onChange={this._onSettingsChange}
                />;
            case FieldDisplayType.Impact:
                return <EnumSliderSettingsEdit
                    ref={this._settingComponent as any}
                    settingsDefaults={impactEnumSliderDefaultSettings}
                />;
        }

        return null;
    }

    private _onSettingsChange = (isValid: boolean) => {
        this.setState({ isDirty: true, isSettingsValid: isValid });
    }

    private _setFieldInfo<K extends keyof IBaseFieldInfo>(changes: Pick<IBaseFieldInfo, K>) {
        this.setState({
            isDirty: true,
            fieldInfo: {
                ...this.state.fieldInfo,
                ...changes
            }
        });
    }

    private _saveClick = () => {
        const { field, onDismiss } = this.props;
        const { fieldInfo, displayType } = this.state;

        if (field) {
            this._update(field, fieldInfo);
        } else {
            this._save(fieldInfo, displayType);
        }

        onDismiss();
    }

    private _update(field: Metadata.Field, fieldInfo: IBaseFieldInfo) {
        const { actions } = this.props;
        if (!actions.updateField) {
            return;
        }

        const update = this._settingComponent.current?.update(fieldInfo) ?? fieldInfo;
        actions.updateField(field.id, update);
    }

    private _save(fieldInfo: IBaseFieldInfo, displayType: FieldDisplayType) {
        const { actions } = this.props;
        if (!actions.saveField)
            return;

        const field: Metadata.ICreateFieldInfo = {
            ...fieldInfo,
            name: fieldInfo.label,
            type: getType(displayType),
            settings: {
                ...fieldInfo.settings,
                ...getDefaultSettings(displayType)
            },
        }

        const defaultValue = getDefaultValue(displayType);
        if (defaultValue !== undefined) {
            field.defaultValue = defaultValue;
        }

        this._settingComponent.current?.save(field);
        actions.saveField(field);
    }
}

function getDefaultValue(displayType: FieldDisplayType): number | string | undefined {
    return displayType === FieldDisplayType.Impact
        ? Impact.NA
        : undefined;
}

const getDefaultSettings = (displayType: FieldDisplayType): Partial<IFieldSettings> | undefined => {
    if (displayType === FieldDisplayType.Richtext) {
        return { multiline: true };
    }

    return undefined;
}

export function getDisplayType(field: Metadata.Field): FieldDisplayType {
    switch (field.type) {
        case Metadata.FieldType.Date:
        case Metadata.FieldType.DateTime:
        case Metadata.FieldType.UniversalDateTime:
            return FieldDisplayType.Date;
        case Metadata.FieldType.Flag:
            return FieldDisplayType.Flag;
        case Metadata.FieldType.Integer:
        case Metadata.FieldType.Decimal:
            if (field.settings?.editControl == "Slider") {
                return FieldDisplayType.Slider;
            }
            if (field.settings?.editControl == "EnumSlider" && field.settings.className == 'impact') {
                return FieldDisplayType.Impact;
            }

            return field.type === Metadata.FieldType.Decimal
                ? FieldDisplayType.Decimal
                : FieldDisplayType.Integer;
        case Metadata.FieldType.Assignment:
            return FieldDisplayType.Assignment;
        case Metadata.FieldType.Resource:
            return FieldDisplayType.Resource;
        case Metadata.FieldType.User:
            return FieldDisplayType.User;
        case Metadata.FieldType.Portfolio:
            return FieldDisplayType.Portfolio;
        case Metadata.FieldType.Project:
            return FieldDisplayType.Project;
        case Metadata.FieldType.Challenge:
            return FieldDisplayType.Challenge;
        case Metadata.FieldType.Idea:
            return FieldDisplayType.Idea;
        case Metadata.FieldType.Group:
            return FieldDisplayType.Group;
        case Metadata.FieldType.CostRateTable:
            return FieldDisplayType.CostRateTable;
        case Metadata.FieldType.Task:
            return FieldDisplayType.Task;
        case Metadata.FieldType.Predecessor:
            return FieldDisplayType.Predecessor;
        case Metadata.FieldType.Text:
            if (field.settings && ["Dropdown", "ColorStatusDropdown", "ColorStageDropdown"].includes(field.settings.editControl)) {
                return FieldDisplayType.Select;
            }
            if (FieldsService.isMultiline(field)) {
                return FieldDisplayType.Richtext;
            }
            return FieldDisplayType.Text;
        case Metadata.FieldType.Hyperlink:
            return FieldDisplayType.Hyperlink;
        default:
            return FieldDisplayType.Text;
    }
}

function getType(displayType: FieldDisplayType): Metadata.FieldType {
    switch (displayType) {
        case FieldDisplayType.Slider: return Metadata.FieldType.Decimal;
        case FieldDisplayType.Select: return Metadata.FieldType.Text;
        case FieldDisplayType.Integer: return Metadata.FieldType.Integer;
        case FieldDisplayType.Decimal: return Metadata.FieldType.Decimal;
        case FieldDisplayType.Text: return Metadata.FieldType.Text;
        case FieldDisplayType.Richtext: return Metadata.FieldType.Text;
        case FieldDisplayType.Date: return Metadata.FieldType.Date;
        case FieldDisplayType.Assignment: return Metadata.FieldType.Assignment;
        case FieldDisplayType.Resource: return Metadata.FieldType.Resource;
        case FieldDisplayType.User: return Metadata.FieldType.User;
        case FieldDisplayType.Flag: return Metadata.FieldType.Flag;
        case FieldDisplayType.Impact: return Metadata.FieldType.Integer;
        case FieldDisplayType.Portfolio: return Metadata.FieldType.Portfolio;
        case FieldDisplayType.Project: return Metadata.FieldType.Project;
        case FieldDisplayType.Challenge: return Metadata.FieldType.Challenge;
        case FieldDisplayType.Idea: return Metadata.FieldType.Idea;
        case FieldDisplayType.Group: return Metadata.FieldType.Group;
        case FieldDisplayType.CostRateTable: return Metadata.FieldType.CostRateTable;
        case FieldDisplayType.Task: return Metadata.FieldType.Task;
        case FieldDisplayType.Hyperlink: return Metadata.FieldType.Hyperlink;
        case FieldDisplayType.Predecessor: return Metadata.FieldType.Predecessor;
        case FieldDisplayType.Program: return Metadata.FieldType.Program;
        case FieldDisplayType.Objective: return Metadata.FieldType.Objective;
        case FieldDisplayType.Roadmap: return Metadata.FieldType.Roadmap;
        case FieldDisplayType.Email: 
        case FieldDisplayType.Clock: 
        case FieldDisplayType.AdministrateApp:
        case FieldDisplayType.ManagePrioritization:
        case FieldDisplayType.ManageBudget:
        case FieldDisplayType.ManageConfiguration:
        case FieldDisplayType.ManageBilling:
        case FieldDisplayType.ManageIntegrations:
        case FieldDisplayType.PPMInsights:
        case FieldDisplayType.ManageResources:
        case FieldDisplayType.ManageResourcePlanning:
        case FieldDisplayType.PresentationMode:
        case FieldDisplayType.LogonAccount:
        case FieldDisplayType.License:
        case FieldDisplayType.AuthProvider:
        case FieldDisplayType.LinkedResource:
            return Metadata.FieldType.Text;
        default:
            const exhaustiveCheck: never = displayType;
    }

    throw new Error(`Can't convert displayType \"${displayType}\"`);
}

const displayTypeDescriptorMap: { [key: number]: { iconName: string; description?: string; label?: string } } = {
    [FieldDisplayType.Text]: {
        iconName: 'PPMXFieldText',
        description: 'Text field is used to store texts up to 255 (single-line) characters'
    },
    [FieldDisplayType.Richtext]: {
        label: 'Rich Text',
        iconName: 'PPMXFieldRichtext',
        description: 'Rich Text field is used to store texts up to 8000 characters (multiline), with support for basic formatting (rich text)'
    },
    [FieldDisplayType.Date]: {
        iconName: 'PPMXFieldDate',
        description: 'Date field is used to store dates, picked from a drop-down calendar'
    },
    [FieldDisplayType.Integer]: {
        iconName: 'PPMXFieldInteger',
        description: 'Integer field is used to store numerical data'
    },
    [FieldDisplayType.Decimal]: {
        iconName: 'PPMXFieldDecimal',
        description: 'Decimal field is used to store non-integer numerical data'
    },
    [FieldDisplayType.User]: {
        iconName: 'PPMXFieldUser',
        description: 'User field is used to assign a PPM Express user'
    },
    [FieldDisplayType.Resource]: {
        iconName: 'PPMXFieldResource',
        description: 'Resource field is used to assign a PPM Express resource'
    },
    [FieldDisplayType.Assignment]: {
        iconName: 'PPMXFieldResource',
        description: 'Assignment field is used to define task assignments'
    },
    [FieldDisplayType.Slider]: {
        iconName: 'PPMXFieldSlider',
        description: 'Slider field is used for numerical values within a defined range'
    },
    [FieldDisplayType.Select]: {
        iconName: 'PPMXFieldSelect',
        description: 'Select field is used to predefine a drop-down list of values to choose from'
    },
    [FieldDisplayType.Flag]: {
        iconName: 'PPMXFieldFlag',
        description: 'Flag field is used to store data that has two distinct values'
    },
    [FieldDisplayType.Impact]: {
        iconName: 'PPMXFieldImpact',
        description: 'Impact field is used to display impact as Low, Normal, Moderate, Strong, and Extreme values'
    },
    [FieldDisplayType.Portfolio]: {
        iconName: 'PPMXPortfolio',
        description: 'Portfolio field is used to assign a PPM Express portfolio'
    },
    [FieldDisplayType.Project]: {
        iconName: 'PPMXProject',
        description: 'Project field is used to assign a PPM Express project'
    },
    [FieldDisplayType.Challenge]: {
        iconName: 'PPMXBusinessChallenge',
        description: 'Challenge field is used to assign a PPM Express challenge'
    },
    [FieldDisplayType.Idea]: {
        iconName: 'PPMXBusinessIdeas',
        description: 'Idea field is used to assign a PPM Express idea'
    },
    [FieldDisplayType.Group]: {
        iconName: 'GroupList',
        description: 'Group field is used to assign a group'
    },
    [FieldDisplayType.CostRateTable]: {
        iconName: 'GroupList',
        description: 'CostRateTable field is used to assign a cost rate table'
    },
    [FieldDisplayType.Task]: {
        iconName: 'PPMXLogo',
        description: 'Task field is used to assign a PPM Express task'
    },
    [FieldDisplayType.Hyperlink]: {
        iconName: 'PPMXFieldHyperlink',
        description: 'Hyperlink field is used for URLs associated with this project, task, item'
    },
    [FieldDisplayType.Predecessor]: {
        iconName: 'PPMXFieldSelect',
        description: 'Predecessor field is used to define task dependencies'
    },
    [FieldDisplayType.Program]: {
        iconName: 'PPMXProgram',
        description: 'Program field is used to assign a PPM Express program'
    },
    [FieldDisplayType.Objective]: {
        iconName: 'PPMXStrategicPriority',
        description: 'Objective field is used to assign a PPM Express objective'
    },
    [FieldDisplayType.Roadmap]: {
        iconName: 'PPMXRoadmap',
        description: 'Roadmap field is used to assign a PPM Express roadmap'
    },
    [FieldDisplayType.Email]: {
        iconName: 'Mail',
        description: 'Email field is used to store email addresses'
    },
    [FieldDisplayType.Clock]: {
        iconName: 'Clock',
    },
    [FieldDisplayType.AdministrateApp]: {
        iconName: 'Admin',
    },
    [FieldDisplayType.ManagePrioritization]: {
        iconName: 'PPMXStrategicPriorities',
    },
    [FieldDisplayType.ManageBudget]: {
        iconName: 'Bank',
    },
    [FieldDisplayType.ManageConfiguration]: {
        iconName: 'PPMXGear',
    },
    [FieldDisplayType.ManageBilling]: {
        iconName: 'PPMXFieldFormatCost',
    },
    [FieldDisplayType.ManageIntegrations]: {
        iconName: 'PlugDisconnected',
    },
    [FieldDisplayType.PPMInsights]: {
        iconName: 'D365CustomerInsights',
    },
    [FieldDisplayType.ManageResources]: {
        iconName: 'PPMXResource',
    },
    [FieldDisplayType.ManageResourcePlanning]: {
        iconName: 'PPMXSectionResourcePlan',
    },
    [FieldDisplayType.PresentationMode]: {
        iconName: 'AnalyticsView',
    },
    [FieldDisplayType.LogonAccount]: {
        iconName: 'PPMXCardManager',
    },
    [FieldDisplayType.License]: {
        iconName: 'PPMXSectionKeyDecisions',
    },
    [FieldDisplayType.AuthProvider]: {
        iconName: 'Encryption',
    },
    [FieldDisplayType.LinkedResource]: {
        iconName: 'PPMXCardManager',
    }
}

export function getDisplayTypeDescriptor(type: FieldDisplayType): { iconName: string; description?: string; label?: string } {
    return displayTypeDescriptorMap[type] || { iconName: undefined, description: undefined, label: undefined };
}